import React, { Component } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { Download, ShieldCheck, AlertTriangle, CheckCircle } from 'lucide-react';
// --- Mock Data ---
const capitalData = {
  held: 8500000,
  required: 6200000,
  ratio: 137,
  status: 'Well Capitalized'
};
const rocByProgram = [{
  program: 'Specialty',
  roc: 24.5
}, {
  program: 'Marine',
  roc: 21.3
}, {
  program: 'Property',
  roc: 18.7
}, {
  program: 'Casualty',
  roc: 16.2
}, {
  program: 'Motor',
  roc: 14.8
}];
const utilizationData = [{
  line: 'Property',
  q1: 45,
  q2: 52,
  q3: 48,
  q4: 55
}, {
  line: 'Casualty',
  q1: 38,
  q2: 42,
  q3: 45,
  q4: 48
}, {
  line: 'Motor',
  q1: 62,
  q2: 58,
  q3: 65,
  q4: 60
}, {
  line: 'Marine',
  q1: 28,
  q2: 32,
  q3: 35,
  q4: 38
}, {
  line: 'Specialty',
  q1: 72,
  q2: 68,
  q3: 75,
  q4: 70
}];
const stressScenarios = [{
  name: 'Base Case',
  description: 'Current capital position',
  capitalRemaining: 8500000,
  coverageRatio: 137,
  status: 'Adequate',
  impact: 0
}, {
  name: 'Moderate Stress',
  description: '1-in-50 year event (-15% capital)',
  capitalRemaining: 7225000,
  coverageRatio: 116,
  status: 'Adequate',
  impact: -15
}, {
  name: 'Severe Stress',
  description: '1-in-200 year event (-30% capital)',
  capitalRemaining: 5950000,
  coverageRatio: 96,
  status: 'Warning',
  impact: -30
}];
// --- Colors ---
const COLORS = {
  primary: '#3B82F6',
  success: '#10B981',
  warning: '#F59E0B',
  neutral: '#6B7280',
  background: '#EFF6FF' // Light Blue
};
// --- Components ---
const CustomTooltip = ({
  active,
  payload,
  label,
  unit = '%'
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-md text-sm" data-id="element-1055">
        <p className="font-semibold text-gray-900 mb-1" data-id="element-1056">{label}</p>
        <p className="text-gray-600" data-id="element-1057">
          ROC:{' '}
          <span className="font-medium text-gray-900" data-id="element-1058">{payload[0].value}%</span>
        </p>
      </div>;
  }
  return null;
};
const GaugeChart = ({
  value,
  min,
  max,
  target
}: {
  value: number;
  min: number;
  max: number;
  target: number;
}) => {
  // Simple SVG gauge implementation
  const radius = 80;
  const stroke = 12;
  const normalizedValue = Math.min(Math.max(value, 0), 200); // Cap at 200% for visual
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - normalizedValue / 200 * circumference;
  // Calculate rotation for semi-circle
  // We want 0% at -180deg (left) and 200% at 0deg (right)
  // But SVG circles start at 3 o'clock (0deg)
  // So we rotate -180deg to start at 9 o'clock
  return <div className="relative flex flex-col items-center justify-center h-48" data-id="element-1059">
      <svg width="200" height="120" viewBox="0 0 200 120" className="overflow-visible" data-id="element-1060">
        {/* Background Arc */}
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="#E5E7EB" strokeWidth={stroke} strokeLinecap="round" data-id="element-1061" />

        {/* Value Arc */}
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke={value >= target ? COLORS.success : value >= min ? COLORS.primary : COLORS.warning} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={`${circumference / 2} ${circumference / 2}`} strokeDashoffset={circumference / 2 * (1 - normalizedValue / 200)} className="transition-all duration-1000 ease-out" data-id="element-1062" />

        {/* Target Marker */}
        <line x1="100" y1="20" x2="100" y2="5" stroke="#6B7280" strokeWidth="2" transform={`rotate(${target / 200 * 180 - 90} 100 100)`} data-id="element-1063" />
        <text x="100" y="0" textAnchor="middle" fontSize="10" fill="#6B7280" transform={`rotate(${target / 200 * 180 - 90} 100 100) translate(0, -5)`} data-id="element-1064">
          Target
        </text>
      </svg>

      <div className="absolute top-20 text-center" data-id="element-1065">
        <div className="text-4xl font-bold text-gray-900" data-id="element-1066">{value}%</div>
        <div className="text-sm font-medium text-gray-500 mt-1" data-id="element-1067">
          Coverage Ratio
        </div>
      </div>
    </div>;
};
const HeatmapCell = ({
  value
}: {
  value: number;
}) => {
  // Calculate opacity based on value (20-80 range mapped to 0.1-1.0)
  const opacity = Math.max(0.1, Math.min(1, (value - 20) / 60));
  return <div className="flex items-center justify-center h-12 rounded text-sm font-medium transition-colors" style={{
    backgroundColor: `rgba(59, 130, 246, ${opacity})`,
    color: opacity > 0.6 ? 'white' : '#1E3A8A'
  }} data-id="element-1068">
      {value}%
    </div>;
};
export function CapitalReturnOnCapital() {
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-1069">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-1070">
        <div data-id="element-1071">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1072">
            Capital & Return on Capital
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-1073">
            Capital adequacy and efficiency analysis • FY 2024
          </p>
        </div>
        <div className="flex items-center gap-4" data-id="element-1074">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-1075">
            <ShieldCheck size={16} className="mr-1.5" data-id="element-1076" />
            {capitalData.status}
          </span>
          <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-1077">
            <Download size={16} className="mr-2" data-id="element-1078" />
            Export Report
          </button>
        </div>
      </div>

      {/* Top Row: Gauge + Scenario Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1079">
        {/* Capital Adequacy Gauge */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1080">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1081">
            Capital Adequacy
          </h2>
          <GaugeChart value={capitalData.ratio} min={100} max={200} target={120} data-id="element-1082" />

          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-center" data-id="element-1083">
            <div data-id="element-1084">
              <p className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-1085">
                Capital Held
              </p>
              <p className="text-lg font-bold text-blue-600" data-id="element-1086">
                £{(capitalData.held / 1000000).toFixed(1)}M
              </p>
            </div>
            <div data-id="element-1087">
              <p className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-1088">
                Required
              </p>
              <p className="text-lg font-bold text-gray-700" data-id="element-1089">
                £{(capitalData.required / 1000000).toFixed(1)}M
              </p>
            </div>
          </div>

          <div className="mt-4 text-center" data-id="element-1090">
            <p className="text-xs text-gray-400" data-id="element-1091">
              Minimum Requirement: 100% • Target: 120%
            </p>
          </div>
        </div>

        {/* Stress Scenario Panel */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1092">
          <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-1093">
            Downside Stress Impact
          </h2>
          <div className="space-y-4" data-id="element-1094">
            {stressScenarios.map((scenario, index) => <div key={index} className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-1095">
                <div className="flex-1" data-id="element-1096">
                  <div className="flex items-center gap-2" data-id="element-1097">
                    <h3 className="font-semibold text-gray-900" data-id="element-1098">
                      {scenario.name}
                    </h3>
                    <span className="text-xs text-gray-500" data-id="element-1099">
                      ({scenario.description})
                    </span>
                  </div>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2" data-id="element-1100">
                    <div className={`h-2 rounded-full ${scenario.status === 'Warning' ? 'bg-amber-500' : 'bg-blue-600'}`} style={{
                  width: `${Math.min(scenario.coverageRatio, 100)}%`
                }} data-id="element-1101"></div>
                  </div>
                </div>

                <div className="ml-8 text-right min-w-[120px]" data-id="element-1102">
                  <p className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-1103">
                    Coverage Ratio
                  </p>
                  <p className={`text-xl font-bold ${scenario.status === 'Warning' ? 'text-amber-600' : 'text-gray-900'}`} data-id="element-1104">
                    {scenario.coverageRatio}%
                  </p>
                </div>

                <div className="ml-8 text-right min-w-[100px]" data-id="element-1105">
                  <p className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-1106">
                    Remaining
                  </p>
                  <p className="text-lg font-semibold text-gray-700" data-id="element-1107">
                    £{(scenario.capitalRemaining / 1000000).toFixed(1)}M
                  </p>
                </div>

                <div className="ml-6 flex items-center justify-center w-8" data-id="element-1108">
                  {scenario.status === 'Warning' ? <AlertTriangle className="text-amber-500" size={20} data-id="element-1109" /> : <CheckCircle className="text-green-500" size={20} data-id="element-1110" />}
                </div>
              </div>)}
          </div>
          <p className="mt-4 text-sm text-gray-500 italic" data-id="element-1111">
            * Even under severe stress scenarios (-30% capital impact), the
            portfolio maintains adequate coverage close to regulatory minimums.
          </p>
        </div>
      </div>

      {/* Middle Row: ROC Bar Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1112">
        <div className="flex justify-between items-center mb-6" data-id="element-1113">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1114">
            Return on Capital by Program
          </h2>
          <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-1115">
            <div className="w-4 h-0.5 bg-gray-400 border-t border-dashed" data-id="element-1116"></div>
            <span data-id="element-1117">Target ROC (15%)</span>
          </div>
        </div>
        <div className="h-64" data-id="element-1118">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1119">
            <BarChart data={rocByProgram} margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5
          }} layout="vertical" data-id="element-1120">
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" data-id="element-1121" />
              <XAxis type="number" hide data-id="element-1122" />
              <YAxis dataKey="program" type="category" axisLine={false} tickLine={false} tick={{
              fill: '#4B5563',
              fontSize: 14,
              fontWeight: 500
            }} width={100} data-id="element-1123" />
              <Tooltip content={<CustomTooltip data-id="element-1125" />} data-id="element-1124" />
              <ReferenceLine x={15} stroke="#9CA3AF" strokeDasharray="3 3" data-id="element-1126" />
              <Bar dataKey="roc" radius={[0, 4, 4, 0]} barSize={32} data-id="element-1127">
                {rocByProgram.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS.primary} data-id="element-1128" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Row: Capital Utilization Heatmap */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1129">
        <div className="flex justify-between items-center mb-6" data-id="element-1130">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1131">
            Capital Utilization by Line of Business
          </h2>
          <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-1132">
            <span data-id="element-1133">Low</span>
            <div className="w-24 h-2 rounded-full bg-gradient-to-r from-blue-50 to-blue-600" data-id="element-1134"></div>
            <span data-id="element-1135">High</span>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-1136">
          <table className="w-full" data-id="element-1137">
            <thead data-id="element-1138">
              <tr data-id="element-1139">
                <th className="text-left py-2 px-4 text-sm font-medium text-gray-500" data-id="element-1140">
                  Line of Business
                </th>
                <th className="text-center py-2 px-4 text-sm font-medium text-gray-500" data-id="element-1141">
                  Q1 2024
                </th>
                <th className="text-center py-2 px-4 text-sm font-medium text-gray-500" data-id="element-1142">
                  Q2 2024
                </th>
                <th className="text-center py-2 px-4 text-sm font-medium text-gray-500" data-id="element-1143">
                  Q3 2024
                </th>
                <th className="text-center py-2 px-4 text-sm font-medium text-gray-500" data-id="element-1144">
                  Q4 2024
                </th>
                <th className="text-center py-2 px-4 text-sm font-medium text-gray-500" data-id="element-1145">
                  Avg Utilization
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-1146">
              {utilizationData.map((row, index) => <tr key={index} data-id="element-1147">
                  <td className="py-3 px-4 text-sm font-medium text-gray-900" data-id="element-1148">
                    {row.line}
                  </td>
                  <td className="py-2 px-4" data-id="element-1149">
                    <HeatmapCell value={row.q1} data-id="element-1150" />
                  </td>
                  <td className="py-2 px-4" data-id="element-1151">
                    <HeatmapCell value={row.q2} data-id="element-1152" />
                  </td>
                  <td className="py-2 px-4" data-id="element-1153">
                    <HeatmapCell value={row.q3} data-id="element-1154" />
                  </td>
                  <td className="py-2 px-4" data-id="element-1155">
                    <HeatmapCell value={row.q4} data-id="element-1156" />
                  </td>
                  <td className="py-2 px-4 text-center text-sm font-bold text-gray-700" data-id="element-1157">
                    {Math.round((row.q1 + row.q2 + row.q3 + row.q4) / 4)}%
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}